/**
 * CSR account allocation — display labels for the allocation reason the
 * server resolves (docs/features/csr-account-allocation/
 * 04_module_update_spec_users_module.md, R5). The server is authoritative;
 * `classifyRawRef2` below exists only so the mock adapter can stand in for
 * that classification, and no page calls it.
 *
 * Buckets are not exempt from needing a named owner (product brief §16,
 * "Resolved decisions" 2026-10-02) — "Online", "House", "SHOP", "Pollards
 * Account Plymouth" and any other non-person value all classify as `bucket`,
 * the same as the literal "Group CSR".
 */

export type AllocationReason = "blank" | "bucket" | "unrecognised" | "left";

const KNOWN_BUCKETS = new Set(
  [
    "Group CSR",
    "Online",
    "House",
    "SHOP",
    "Pollards Account Plymouth",
    "Ready To Invoice",
    "Legal & Prof.",
    "Other - Non Classifiable",
  ].map((v) => v.toLowerCase()),
);

/** Matches job-code-looking junk such as "D.0313.004" / "H.0176.016". */
const JOB_CODE_PATTERN = /^[A-Z]\.\d{4}\.\d{3}$/i;

export interface AllocationClassification {
  reason: AllocationReason;
  /** The bucket label to show as secondary text, e.g. "Online" — only set
   *  when `reason === "bucket"`. */
  bucketLabel?: string;
}

/**
 * Classify a raw `Ref2` value against the set of current CSR names. A name
 * that resolves to a *left* CSR takes priority over looking bucket-like or
 * blank (a left user's raw value might coincidentally look like a bucket —
 * `left` must still win, per R5).
 */
export function classifyRawRef2(
  rawRef2: string | null | undefined,
  opts: { isLeaverName?: (name: string) => boolean } = {},
): AllocationClassification {
  const raw = (rawRef2 ?? "").trim();
  if (opts.isLeaverName?.(raw)) {
    return { reason: "left" };
  }
  if (raw === "") {
    return { reason: "blank" };
  }
  if (KNOWN_BUCKETS.has(raw.toLowerCase())) {
    return { reason: "bucket", bucketLabel: raw };
  }
  if (JOB_CODE_PATTERN.test(raw)) {
    return { reason: "unrecognised" };
  }
  return { reason: "unrecognised" };
}

export function allocationReasonLabel(
  reason: AllocationReason,
  opts: { leaverName?: string } = {},
): string {
  switch (reason) {
    case "blank":
      return "Blank";
    case "bucket":
      return "Group CSR / no named person";
    case "unrecognised":
      return "Not recognised";
    case "left":
      return opts.leaverName ? `Left: ${opts.leaverName}` : "Left";
  }
}

export type SuggestionBand = "strong" | "weak" | "none";

export function suggestionBandLabel(band: SuggestionBand): string {
  switch (band) {
    case "strong":
      return "Strong";
    case "weak":
      return "Possible";
    case "none":
      return "No suggestion";
  }
}
